<template>
    <div v-if="isVisible && editableProduct" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
            <h2>編輯商品</h2>
            <form @submit.prevent="handleSubmit">
                <div class="form-layout-container">
                    <div class="form-left-panel">
                        <div class="form-group">
                            <label for="editProductImages">商品圖片 (可多選，請選擇一張作為封面):</label>
                            <input type="file" id="editProductImages" @change="handleFileChange" multiple accept="image/*">
                            
                            <div class="image-preview-container">
                                <div v-for="(image, index) in displayedExistingImages" :key="'existing-' + image.url" class="image-preview-item">
                                    <img :src="`${backendBaseUrl}/${image.url}`" :alt="'舊圖片 ' + (index + 1)" class="preview-thumbnail">
                                    <div class="image-actions">
                                        <label :for="'cover-existing-' + index" class="cover-label">
                                            <input
                                                type="radio"
                                                :id="'cover-existing-' + index"
                                                name="coverImageChoice"
                                                :value="image.url"
                                                v-model="chosenMainImageUrl"
                                            > 封面
                                        </label>
                                        <button type="button" @click="removeExistingImage(index)" class="btn-remove-img">×</button>
                                    </div>
                                </div>

                                <div v-if="selectedImages.length > 0" class="new-images-indicator">新上傳/替換的圖片:</div>
                                <div v-for="(image, index) in selectedImages" :key="'new-' + index" class="image-preview-item">
                                    <img :src="image.previewUrl" :alt="'新預覽 ' + (index + 1)" class="preview-thumbnail">
                                    <div class="image-actions">
                                        <label :for="'cover-new-' + index" class="cover-label">
                                            <input
                                                type="radio"
                                                :id="'cover-new-' + index"
                                                name="coverImageChoice" 
                                                :value="image.file.name"
                                                v-model="chosenMainImageUrl"
                                            > 封面
                                        </label>
                                        <button type="button" @click="removeNewImage(index)" class="btn-remove-img">×</button>
                                    </div>
                                </div>
                            </div>
                             <p v-if="!chosenMainImageUrl && (displayedExistingImages.length > 0 || selectedImages.length > 0)" class="cover-warning">
                                請選擇一張圖片作為封面圖！
                            </p>
                        </div>
                    </div>

                    <div class="form-right-panel">
                        <div class="form-group">
                            <label for="editProductId">商品ID:</label>
                            <input type="text" id="editProductId" :value="editableProduct.id" readonly class="readonly-field">
                        </div>
                        <div class="form-group">
                            <label for="editProductName">商品名稱:</label>
                            <input type="text" id="editProductName" v-model="editableProduct.GoodsName" required>
                        </div>
                        <div class="form-group">
                            <label for="editProductBrand">品牌名稱:</label>
                            <input type="text" id="editProductBrand" v-model.trim="editableProduct.brandName" placeholder="輸入品牌名稱..." required>
                        </div>
                        <div class="form-group">
                            <label for="editProductGender">適用性別:</label>
                            <select id="editProductGender" v-model="editableProduct.gender">
                                <option v-for="genderItem in GenderOptions" :key="genderItem.value" :value="genderItem.value">
                                    {{ genderItem.label }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label for="editProductModel">商品型號:</label>
                            <input type="text" id="editProductModel" v-model="editableProduct.model" placeholder="輸入商品型號...">
                        </div>
                        <div class="form-group">
                            <label for="editProductPrice">一般價 (元):</label>
                            <input type="number" id="editProductPrice" v-model.number="editableProduct.normal_price" step="0.01" required>
                        </div>
                        <div class="form-group">
                            <label for="editProductPrice">會員價 (元):</label>
                            <input type="number" id="editProductPrice" v-model.number="editableProduct.price" step="0.01" required>
                        </div>
                        <div class="form-group">
                            <label for="editProductStatus">狀態:</label>
                            <select id="editProductStatus" v-model="editableProduct.status" required>
                                <option value="on">上架中</option>
                                <option value="off">已下架</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label for="editProductDescription">商品介紹:</label>
                            <textarea id="editProductDescription" v-model="editableProduct.description" rows="4" placeholder="輸入商品詳細介紹..."></textarea>
                        </div>
                        <div class="form-group">
                            <label>商品尺碼和庫存:</label>
                            <div class="shoe-size-input-group">
                                <input type="text" v-model.trim="newShoeSize.size" placeholder="輸入尺碼 (如: 42)" class="shoe-size-field">
                                <input type="number" v-model.number="newShoeSize.stock" placeholder="庫存" min="0" class="shoe-stock-field">
                                <button type="button" @click="addShoeSize" class="btn btn-add-size">添加尺碼</button>
                            </div>
                            <div v-if="editableProduct.shoeSizes && editableProduct.shoeSizes.length > 0" class="shoe-size-list">
                                <strong>已添加尺碼:</strong>
                                <ul>
                                    <li v-for="(item, index) in editableProduct.shoeSizes" :key="item.size || index" class="shoe-size-list-item">
                                        <span>尺碼: {{ item.size }} - 庫存: </span>
                                        <input 
                                            type="number" 
                                            v-model.number="item.stock" 
                                            min="0" 
                                            class="shoe-size-list-stock-input"
                                            @blur="validateStock(item)"
                                        >
                                        <button type="button" @click="removeShoeSize(index)" class="btn-remove-size">×</button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <div class="form-group">
                            <label for="editProductTags">標籤 (逗號分隔):</label>
                            <input type="text" id="editProductTags" v-model="tagsInput" placeholder="新品,熱賣">
                        </div>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-cancel" @click="closeModal">取消</button>
                    <button type="submit" class="btn btn-save" :disabled="isSaveDisabled">保存更改</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, computed } from 'vue';
import { ElMessage } from 'element-plus'

const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL;

const props = defineProps({
    isVisible: Boolean,
    product: Object
    // categoriesList 和 brandsList props 已移除
});

const emit = defineEmits(['close', 'save']);

const GenderOptions = [
  {label: '男士',value: 'men'},
  {label: '女士',value:'female'},
  {label: '情侶',value:'neutral'}
];

const getInitialEditableProduct = () => ({
    id: '', 
    name: '',
    // categoryId: '', // 移除
    brandName: '',    // 改为 brandName
    kind: '',
    gender: '',
    model: '',
    normal_price: 0,
    price: 0,
    status: 'on',
    mainImage: '',
    images: [], 
    description: '',
    tags: [],
    shoeSizes: []
});


const editableProduct = ref(getInitialEditableProduct());
const tagsInput = ref('');
const selectedImages = ref([]);
const displayedExistingImages = ref([]);
const deletedExistingImageUrls = ref([]);
const chosenMainImageUrl = ref('');
const newShoeSize = ref({ size: '', stock: 0 });

watch(() => [props.product, props.isVisible], ([newProductData, newIsVisible]) => {
    if (newIsVisible && newProductData) {
        editableProduct.value = {
            ...getInitialEditableProduct(), 
            ...newProductData,
            brandName: newProductData.brandName || (newProductData.brand ? newProductData.brand.name : '') || '', // 兼容旧数据或对象形式
            kind: newProductData.kindName || '', // 兼容旧数据或对象形式
            shoeSizes: newProductData.shoeSizes ? JSON.parse(JSON.stringify(newProductData.shoeSizes)) : []
        };
        editableProduct.value.shoeSizes.forEach(size => {
            if (typeof size.stock === 'undefined') size.stock = 0;
        });

        tagsInput.value = editableProduct.value.tags ? editableProduct.value.tags.join(', ') : '';
        
        selectedImages.value = [];
        deletedExistingImageUrls.value = [];
        let mainImgFromProp = typeof editableProduct.value.mainImage === 'string' ? editableProduct.value.mainImage : '';
        chosenMainImageUrl.value = mainImgFromProp;

        const allOldImageUrls = Array.isArray(newProductData.images)
            ? newProductData.images
                .filter(img => img && typeof img.file_path === 'string')
                .map(img => img.file_path)
            : [];
        const tempDisplayedImages = new Set();
        if (mainImgFromProp) {
            tempDisplayedImages.add(mainImgFromProp);
        }
        allOldImageUrls.forEach(url => tempDisplayedImages.add(url));

        displayedExistingImages.value = Array.from(tempDisplayedImages).map(url => ({ url: url }));

        newShoeSize.value = { size: '', stock: 0 };
    } else if (!newIsVisible) {
        editableProduct.value = getInitialEditableProduct();
        tagsInput.value = '';
        selectedImages.value = [];
        displayedExistingImages.value = [];
        deletedExistingImageUrls.value = [];
        chosenMainImageUrl.value = '';
        newShoeSize.value = { size: '', stock: 0 };
    }
}, { immediate: true, deep: true });

const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {

        if (file.name.includes(',') || file.name.includes('，')) {
            ElMessage.warning(`文件名 "${file.name}" 不能包含逗号 ("," 或 "，")。请修改文件名后重新上传。`);
            return; // 阻止此文件的后续处理
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/jfif'];
        if (!allowedTypes.includes(file.type)) {
            ElMessage.warning(`不支持的文件类型: "${file.name}"。请上传 JPEG, PNG, JPG, 或 JFIF 格式的图片。`);
            return; // 阻止此文件的后续处理
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            ElMessage.warning(`文件 ${file.name} 大小超过限制 (5MB)。`);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const newImage = { file: file, previewUrl: e.target.result, filename: file.name };
            selectedImages.value.push(newImage);
            if (!chosenMainImageUrl.value || displayedExistingImages.value.some(img => img.url === chosenMainImageUrl.value)) {
                 if(selectedImages.value.length === 1) chosenMainImageUrl.value = newImage.file.name;
            }
        };
        reader.readAsDataURL(file);
    });
    event.target.value = null;
};

const removeNewImage = (indexToRemove) => {
    const removedImage = selectedImages.value[indexToRemove];
    selectedImages.value.splice(indexToRemove, 1);
    if (chosenMainImageUrl.value === removedImage.filename) {
        chosenMainImageUrl.value = ''; 
        if (selectedImages.value.length > 0) {
            chosenMainImageUrl.value = selectedImages.value[0].previewUrl;
        } else if (displayedExistingImages.value.length > 0) {
            chosenMainImageUrl.value = displayedExistingImages.value[0].url;
        }
    }
};

const removeExistingImage = (indexToRemove) => {
    const removedImage = displayedExistingImages.value[indexToRemove];
    deletedExistingImageUrls.value.push(removedImage.url); 
    displayedExistingImages.value.splice(indexToRemove, 1);
    if (chosenMainImageUrl.value === removedImage.url) {
        chosenMainImageUrl.value = ''; 
        if (displayedExistingImages.value.length > 0) {
            chosenMainImageUrl.value = displayedExistingImages.value[0].url;
        } else if (selectedImages.value.length > 0) {
             chosenMainImageUrl.value = selectedImages.value[0].previewUrl;
        }
    }
};


const addShoeSize = () => {
    const size = newShoeSize.value.size;
    const stock = newShoeSize.value.stock;
    if (!size) { ElMessage.warning("請輸入尺碼！"); return; }
    if (stock < 0 || stock === null || stock === undefined || isNaN(stock)) {
        newShoeSize.value.stock = 0; 
        ElMessage.warning("庫存數量已設為0，原輸入無效。");
    }
    if (editableProduct.value.shoeSizes.some(item => item.size === size)) {
        ElMessage.warning(`尺碼 "${size}" 已存在！`); return;
    }
    editableProduct.value.shoeSizes.push({ size: size, stock: Number(newShoeSize.value.stock) });
    newShoeSize.value = { size: '', stock: 0 };
};

const removeShoeSize = (indexToRemove) => {
    editableProduct.value.shoeSizes.splice(indexToRemove, 1);
};

const validateStock = (shoeSizeItem) => {
    if (shoeSizeItem.stock < 0 || shoeSizeItem.stock === null || typeof shoeSizeItem.stock === 'undefined' || isNaN(shoeSizeItem.stock)) {
        ElMessage.warning(`尺碼 "${shoeSizeItem.size}" 的庫存輸入無效，將自動更正為0。`);
        shoeSizeItem.stock = 0;
    }
};

const isSaveDisabled = computed(() => {
    const noCoverSelected = !chosenMainImageUrl.value && (displayedExistingImages.value.length > 0 || selectedImages.value.length > 0);
    const invalidStock = editableProduct.value.shoeSizes.some(
        item => item.stock < 0 || item.stock === null || typeof item.stock === 'undefined' || isNaN(item.stock)
    );
    // 检查品牌名称是否已输入
    const brandNotEntered = !editableProduct.value.brandName;

    return noCoverSelected || invalidStock || brandNotEntered;
});

const closeModal = () => {
    emit('close');
};

const handleSubmit = () => {
    for (const shoe of editableProduct.value.shoeSizes) {
        if (shoe.stock < 0 || shoe.stock === null || typeof shoe.stock === 'undefined' || isNaN(shoe.stock)) {
            ElMessage.warning(`尺碼 "${shoe.size}" 的庫存 (${shoe.stock}) 無效。請更正後保存。`);
            return;
        }
    }

    if (!chosenMainImageUrl.value && (displayedExistingImages.value.length > 0 || selectedImages.value.length > 0)) {
        ElMessage.warning("請選擇一張圖片作為封面！");
        return;
    }
    // 检查品牌名称是否已输入
    if (!editableProduct.value.brandName) {
        ElMessage.warning("請輸入品牌名稱！");
        return;
    }

    editableProduct.value.tags = tagsInput.value ? tagsInput.value.split(/[,，]/).map(tag => tag.trim()).filter(tag => tag) : [];
    const productDataToSave = {
        ...editableProduct.value,
        newImageFiles: selectedImages.value.map(img => img.file),
        mainImage: chosenMainImageUrl.value, 
        deletedImageUrls: [...deletedExistingImageUrls.value], 
        shoeSizes: editableProduct.value.shoeSizes.map(s => ({...s, stock: Number(s.stock)}))
    };
    
    delete productDataToSave.images; 

    emit('save', productDataToSave);
};
</script>

<style scoped>
/* 原有样式 - 大部分不变 */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: white;
    padding: 25px 30px;
    border-radius: 8px;
    width: 60%; 
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    max-height: 90vh;
    overflow-y: auto; 
    display: flex; 
    flex-direction: column;
}

.modal-content h2 {
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 1.5em;
    color: #333;
    text-align: center; 
}

.form-layout-container {
    display: flex;
    gap: 20px; 
    flex-grow: 1; 
    overflow-y: hidden; 
}

.form-left-panel {
    flex: 0 0 280px; 
    padding-right: 15px; 
    border-right: 1px solid #eee; 
}

.form-right-panel {
    flex: 1; 
}
.new-images-indicator {
    width:100%;
    margin-top: 10px;
    margin-bottom: 5px;
    color: #007bff;
    font-size:0.9em;
    font-weight: bold;
}


.form-group {
    margin-bottom: 18px;
}

.form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #555;
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group input[type="url"],
.form-group input[type="file"],
.form-group select,
.form-group textarea { 
    width: 100%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: 1em;
    font-family: inherit;
}
.form-group input[type="file"] {
    padding: 5px;
}
.form-group textarea {
    resize: vertical; 
    min-height: 80px; 
}
.readonly-field {
    background-color: #f8f9fa;
    color: #495057;
    cursor: not-allowed;
}

.form-group input:focus, 
.form-group select:focus,
.form-group textarea:focus { 
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.image-preview-container {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 5px 0; 
    max-height: 500px; 
    overflow-y: auto;
}

.image-preview-item {
    position: relative;
    border: 1px solid #eee;
    padding: 5px;
    border-radius: 4px;
    background-color: #f9f9f9;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: calc(50% - 5px); 
    box-sizing: border-box;
}

.preview-thumbnail {
    width: 100%; 
    height: 100px; 
    object-fit: cover;
    border-radius: 4px;
    margin-bottom: 5px;
}

.image-actions {
    display: flex;
    justify-content: space-between; 
    align-items: center;
    width: 100%;
    font-size: 0.9em;
}
.cover-label {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: normal;
    margin-bottom: 0;
}
.cover-label input[type="radio"] {
    width: auto;
    margin-right: 3px;
}

.btn-remove-img {
    background-color: #ff4d4f;
    color: white;
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    font-size: 12px;
    line-height: 18px; 
    text-align: center;
    cursor: pointer;
    padding: 0;
}
.btn-remove-img:hover {
    background-color: #d9363e;
}

.cover-warning {
    color: red;
    font-size: 0.9em;
    margin-top: 5px;
}

.modal-actions {
    margin-top: 25px; 
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 15px; 
    border-top: 1px solid #eee; 
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1em;
    font-weight: 500;
}

.btn-save {
    background-color: #007bff; 
    color: white;
}
.btn-save:hover:not(:disabled) {
    background-color: #0069d9;
}
.btn-save:disabled {
    background-color: #b3d7ff; 
    cursor: not-allowed;
}

.btn-cancel {
    background-color: #6c757d;
    color: white;
}
.btn-cancel:hover {
    background-color: #5a6268;
}

.shoe-size-input-group {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
}
.shoe-size-field {
    flex: 2;
}
.shoe-stock-field {
    flex: 1;
    min-width: 70px;
}
.btn-add-size {
    padding: 8px 10px;
    font-size: 0.9em;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
}
.btn-add-size:hover {
    background-color: #0056b3;
}

.shoe-size-list {
    margin-top: 10px;
    padding-top: 5px;
    max-height: 150px; 
    overflow-y: auto;
}
.shoe-size-list strong {
    display: block;
    margin-bottom: 5px;
    font-size: 0.9em;
}
.shoe-size-list ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
}
.shoe-size-list-item {
    display: flex;
    align-items: center;
    padding: 4px 0;
    font-size: 0.9em;
    border-bottom: 1px solid #f0f0f0;
}
.shoe-size-list-item:last-child {
    border-bottom: none;
}
.shoe-size-list-stock-input {
    width: 10% !important; 
    padding: 4px 6px; 
    font-size: 0.9em;
    text-align: right; 
    border: 1px solid #ddd;
    border-radius: 3px;
    margin-left: 5px; 
    margin-right: auto; 
}


.btn-remove-size {
    background-color: #e74c3c;
    color: white;
    border: none;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 0.8em;
    cursor: pointer;
}
.btn-remove-size:hover {
    background-color: #c0392b;
}
</style>